import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  FileInfo,
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import {
  CarLeasing,
  CarLeasingNoCarDepency,
  CarLeasingStats,
  CarLeasingVersion,
  InsertCarLeasing,
  UpdateCarLeasing,
} from "./types";

export const useCarLeasingApi = () => {
  const api = useApi();

  const getAdminCarLeasing = useCallback(
    (carId: string, signal: AbortSignal) => {
      const url = `/admin/car_leasings/${carId}`;
      return useSWRMutation<CarLeasing | null, CustomError, string>(url, (url) => api.get<CarLeasing>({ url, signal }));
    },
    [api],
  );

  const useAdminCarLeasing = useCallback(
    <K extends keyof CarLeasingNoCarDepency>(
      query: PaginationQueryWithFields<NestedPick<CarLeasingNoCarDepency, K>>,
      signal: AbortSignal,
      notificationsOnly = false,
    ) => {
      const baseUrl = buildUrl<NestedPick<CarLeasingNoCarDepency, K>>("/admin/car_leasings", query);
      const url = notificationsOnly ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}notifications=1` : baseUrl;
      return useSWR<Paginated<NestedPick<CarLeasingNoCarDepency, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<CarLeasingNoCarDepency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const updateAdminCarLeasing = useCallback(
    async (id: string, body: UpdateCarLeasing) => {
      const url = `/admin/car_leasings/${id}`;
      await api.put<UpdateCarLeasing>(url, body);
      api.mutateCache([url, `/admin/car_leasings`, `/admin/car_leasings/${id}/versions`]);
    },
    [api],
  );

  const insertAdminCarLeasing = useCallback(
    async (body: InsertCarLeasing) => {
      const url = `/admin/car_leasings`;
      await api.postNoResult<InsertCarLeasing>(url, body);
      api.mutateCache([url, `/admin/cars/${body.carId}`, `/admin/cars/${body.carId}/overview`]);
    },
    [api],
  );

  const deleteAdminCarLeasing = useCallback(
    async (carId: string): Promise<void> => {
      const url = `/admin/car_leasings/${carId}`;
      await api.del(url);
      api.mutateCache([`/admin/car_leasings`]);
    },
    [api],
  );

  const getAdminCarLeasingHistory = useCallback(
    (query: PaginationQuery, carID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/car_leasings/${carID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminCarLeasingVersion = useCallback(
    (carID: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/car_leasings/${carID}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<CarLeasingVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<CarLeasingVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/car_leasings/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const useCarLeasingStats = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/car_leasings/stats", query);
      return useSWR<CarLeasingStats | null, CustomError, string>(url, (url) => api.get<CarLeasingStats>({ url, signal }));
    },
    [api],
  );

  const exportExcel = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/car_leasings", query);
      const h = new Headers();
      h.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  return {
    deleteAdminCarLeasing,
    insertAdminCarLeasing,
    updateAdminCarLeasing,
    useAdminCarLeasing,
    getAdminCarLeasing,
    getAdminCarLeasingHistory,
    getAdminCarLeasingVersion,
    getFieldFilters,
    useCarLeasingStats,
    exportExcel,
  };
};
